import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPrizes } from '../../redux/actions/spinWin.actions'
import { Prize } from '../../redux/reducers/spinWin.reducer'
import '../../styles/pages/Do2DoSpinWin.scss'

interface RootState {
    spinWin: {
        prizes: Prize[]
        loading: boolean
        error: string | null
    }
}

const textColors = ['#ffffff', '#26345b', '#173c34', '#61253c', '#ffffff', '#492b1b', '#133b5c', '#542f53']

export default function SpinWinWheel() {
    const dispatch = useDispatch<any>()

    const {
        prizes = [],
        loading = false,
        error = null,
    } = useSelector(
        (state: RootState) =>
            state.spinWin ?? {
                prizes: [],
                loading: false,
                error: null,
            }
    )

    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const gameRef = useRef<HTMLElement | null>(null)
    const introRef = useRef<HTMLDivElement | null>(null)
    const animationRef = useRef<number | null>(null)
    const rotationRef = useRef(0)
    const spinningRef = useRef(false)
    const hasSpunRef = useRef(false)

    const [spinning, setSpinning] = useState(false)
    const [hasSpun, setHasSpun] = useState(false)
    const [status, setStatus] = useState('Ready to spin')
    const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [copyLabel, setCopyLabel] = useState('COPY CODE')

    // Load prize data through the Redux action.
    useEffect(() => {
        dispatch(fetchPrizes()).catch(() => {
            // The fetch error is stored in Redux and shown below.
        })
    }, [dispatch])

    const drawWheel = useCallback(
        (angle = rotationRef.current) => {
            const canvas = canvasRef.current
            const ctx = canvas?.getContext('2d')

            if (!canvas || !ctx || prizes.length === 0) return

            const size = canvas.width
            const center = size / 2
            const radius = size / 2 - 10
            const segment = (Math.PI * 2) / prizes.length

            ctx.clearRect(0, 0, size, size)

            prizes.forEach((prize, i) => {
                const start = angle + i * segment - Math.PI / 2
                const end = start + segment

                ctx.beginPath()
                ctx.moveTo(center, center)
                ctx.arc(center, center, radius, start, end)
                ctx.closePath()
                ctx.fillStyle = prize.color
                ctx.fill()

                ctx.strokeStyle = '#ffffff'
                ctx.lineWidth = 7
                ctx.stroke()

                const mid = start + segment / 2

                ctx.save()
                ctx.translate(center + Math.cos(mid) * radius * 0.67, center + Math.sin(mid) * radius * 0.67)
                ctx.rotate(mid + Math.PI / 2)
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'

                ctx.font = 'bold 31px system-ui, sans-serif'
                ctx.fillStyle = textColors[i % textColors.length]
                ctx.fillText(prize.icon, 0, -18)

                ctx.font = '900 22px system-ui, sans-serif'
                ctx.fillText(prize.label, 0, 20)
                ctx.restore()
            })

            ctx.beginPath()
            ctx.arc(center, center, radius, 0, Math.PI * 2)
            ctx.strokeStyle = '#ffffff'
            ctx.lineWidth = 10
            ctx.stroke()
        },
        [prizes]
    )

    // Redraw the wheel when the prize data loads or changes.
    useEffect(() => {
        drawWheel()

        return () => {
            if (animationRef.current !== null) {
                cancelAnimationFrame(animationRef.current)
            }
        }
    }, [drawWheel])

    const spin = useCallback(() => {
        if (spinningRef.current || hasSpunRef.current || loading || prizes.length === 0) {
            return
        }

        spinningRef.current = true
        setSpinning(true)
        setStatus('Good luck!')

        // Demo only: the browser chooses the prize.
        // A production backend should determine the winning prize.
        const prizeIndex = Math.floor(Math.random() * prizes.length)
        const segment = (Math.PI * 2) / prizes.length
        const extraTurns = 5 + Math.random() * 3

        const targetAngle = Math.PI * 2 * extraTurns + (Math.PI * 2 - (prizeIndex + 0.5) * segment)

        const startRotation = rotationRef.current
        const endRotation = startRotation + targetAngle
        const startTime = performance.now()
        const duration = 4200

        const animate = (now: number) => {
            const progress = Math.min((now - startTime) / duration, 1)

            const eased = 1 - Math.pow(1 - progress, 4)

            rotationRef.current = startRotation + (endRotation - startRotation) * eased

            drawWheel(rotationRef.current)

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate)
            } else {
                rotationRef.current %= Math.PI * 2
                drawWheel(rotationRef.current)

                spinningRef.current = false
                hasSpunRef.current = true

                setSpinning(false)
                setHasSpun(true)
                setStatus('Reward revealed')
                setSelectedPrize(prizes[prizeIndex])
                setModalOpen(true)
            }
        }

        animationRef.current = requestAnimationFrame(animate)
    }, [drawWheel, loading, prizes])

    const closeModal = () => {
        setModalOpen(false)
    }

    const copyCode = async () => {
        if (!selectedPrize) return

        try {
            await navigator.clipboard.writeText(selectedPrize.code)
            setCopyLabel('COPIED!')
        } catch {
            setCopyLabel('SELECT CODE')
        }

        window.setTimeout(() => setCopyLabel('COPY CODE'), 1500)
    }

    return (
        <div className="d2d-page">
            <header className="d2d-topbar">
                <div className="d2d-brand">
                    <div className="d2d-brand-mark">✦</div>
                    <div>
                        do2do<span>.eu</span>
                    </div>
                </div>

                <div className="d2d-top-pill">SHOP • PLAY • GET REWARDED</div>
            </header>

            <main>
                <section className="d2d-hero">
                    <div className="d2d-intro" ref={introRef}>
                        <div className="d2d-eyebrow">✦ A little extra joy</div>

                        <h1>
                            Good things
                            <br />
                            come to those
                            <br />
                            who <em>spin.</em>
                        </h1>

                        <p className="d2d-lead">
                            Give the wheel a whirl for a chance to unlock a little something extra on your next order.
                            Your next favourite reward could be one spin away.
                        </p>

                        <div className="d2d-benefits">
                            <div className="d2d-benefit">🎁 Free products</div>
                            <div className="d2d-benefit">🏷️ Shopping discounts</div>
                            <div className="d2d-benefit">🪙 Reward points</div>
                        </div>

                        <button
                            className="d2d-primary"
                            onClick={() => {
                                gameRef.current?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                })

                                if (!hasSpunRef.current) {
                                    canvasRef.current?.focus({
                                        preventScroll: true,
                                    })
                                }
                            }}
                        >
                            Let's play <span aria-hidden="true">↗</span>
                        </button>

                        <div className="d2d-micro">Demo experience · Rewards shown here are for preview only.</div>
                    </div>

                    <section className="d2d-game-side" ref={gameRef} aria-label="Spin and win game">
                        <div className="d2d-game-label">
                            <strong>Your reward is waiting</strong>
                            <span className="d2d-live">{loading ? 'Loading prizes…' : status}</span>
                        </div>

                        <div className="d2d-wheel-wrap" aria-label="Prize wheel">
                            <div className="d2d-wheel-ring" />

                            <canvas
                                ref={canvasRef}
                                className="d2d-wheel-canvas"
                                width={700}
                                height={700}
                                aria-label={`Prize wheel with ${prizes.length} possible rewards`}
                                tabIndex={0}
                            />

                            <div className="d2d-pointer" />

                            <div className="d2d-wheel-center">
                                <span>
                                    SPIN
                                    <br />
                                    &amp; WIN
                                </span>
                            </div>
                        </div>

                        {error && (
                            <p role="alert" className="d2d-error">
                                Unable to load prizes: {error}
                            </p>
                        )}

                        <div className="d2d-spin-row">
                            <button
                                className="d2d-spin-btn"
                                onClick={spin}
                                disabled={loading || prizes.length === 0 || spinning || hasSpun}
                            >
                                {loading
                                    ? 'LOADING…'
                                    : spinning
                                      ? 'SPINNING…'
                                      : hasSpun
                                        ? 'SPIN USED'
                                        : prizes.length === 0
                                          ? 'NO PRIZES'
                                          : 'SPIN THE WHEEL'}
                            </button>

                            <div className="d2d-spin-note">
                                One spin.
                                <br />
                                One little surprise.
                            </div>
                        </div>
                    </section>
                </section>

                <section className="d2d-prize-strip" aria-label="Possible rewards">
                    {prizes.slice(0, 4).map((prize) => (
                        <div className="d2d-prize-card" key={prize.label}>
                            <div className="d2d-prize-icon">{prize.icon}</div>

                            <div>
                                <strong>{prize.label}</strong>
                                <small>{prize.description}</small>
                            </div>
                        </div>
                    ))}
                </section>
            </main>

            <footer className="d2d-footer">
                <span>© Do2Do · Spin &amp; Win demo</span>
                <span>Rewards, eligibility and terms can be configured for your campaign.</span>
            </footer>

            {modalOpen && selectedPrize && (
                <div
                    className="d2d-toast"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            closeModal()
                        }
                    }}
                >
                    <div className="d2d-modal" role="dialog" aria-modal="true" aria-labelledby="d2d-result-title">
                        <button className="d2d-close" onClick={closeModal} aria-label="Close">
                            ×
                        </button>

                        <div className="d2d-win-icon">{selectedPrize.icon}</div>

                        <h2 id="d2d-result-title">{selectedPrize.title}</h2>

                        <p>{selectedPrize.description}</p>

                        <div className="d2d-coupon">
                            <code>{selectedPrize.code}</code>

                            <button className="d2d-copy" onClick={copyCode}>
                                {copyLabel}
                            </button>
                        </div>

                        <button
                            className="d2d-primary d2d-continue"
                            onClick={() => {
                                closeModal()
                                introRef.current?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                })
                            }}
                        >
                            Continue shopping ↗
                        </button>

                        <p className="d2d-fineprint">
                            Demo only: this code is not a valid voucher. Connect a backend to issue real rewards.
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
